/**
 * discoverDraft — per-device persistence for the Discover sheet (subtask #86bbm53r2), so a browser
 * refresh doesn't throw away an in-progress discovery.
 *
 * What it stores (keyed by device, in sessionStorage — TAB-scoped, never written to disk): the user's
 * draft SCOPE (`add` or `replace:<camN>`) plus staged SELECTIONS ({ ip -> { name, recUrl, liveUrl } }).
 * The scope prevents an add draft or another camera's replacement draft from entering the current flow.
 * Those stream URLs can embed the camera's RTSP
 * CREDENTIALS (from the ONVIF probe), which is why this is sessionStorage, not localStorage: it survives a
 * page refresh (the point) but clears on tab close, so credentials never linger at rest. The scan RESULTS are deliberately NOT stored — they
 * re-fetch instantly from the device's discovery cache when the sheet reopens, so persisting a
 * possibly-stale 22-camera list would just risk showing outdated data. An explicit close (Cancel / ×)
 * or an "Add to batch" clears the draft; only a refresh (which never fires those) leaves it to restore.
 *
 * Fail-soft: sessionStorage can be unavailable (private mode / quota) — every call swallows errors so a
 * storage failure never breaks discovery.
 */

const KEY = (deviceId) => `dl_discover_draft_${deviceId}`;

// sessionStorage (NOT localStorage) — the staged picks include stream URLs that can embed the camera's
// RTSP credentials (see the module doc), so the draft must never be written to disk. sessionStorage still
// survives a page REFRESH (the feature's whole purpose) but is cleared when the tab closes, so credentials
// never persist at rest. Guarded for SSR/tests where it may be absent.
const store = () => (typeof sessionStorage !== 'undefined' ? sessionStorage : null);

/** The saved draft for a device, or {} if none / unreadable. */
export const loadDiscoverDraft = (deviceId) => {
  if (!deviceId) return {};
  try {
    const s = store();
    const raw = s && s.getItem(KEY(deviceId));
    const d = raw ? JSON.parse(raw) : null;
    return d && typeof d === 'object' ? d : {};
  } catch (e) {
    return {};
  }
};

/** Merge `patch` into the device's draft (e.g. { open } or { selected }). */
export const saveDiscoverDraft = (deviceId, patch) => {
  if (!deviceId) return;
  try {
    const s = store();
    if (s) s.setItem(KEY(deviceId), JSON.stringify({ ...loadDiscoverDraft(deviceId), ...patch }));
  } catch (e) {
    /* quota / disabled — non-fatal */
  }
};

/** Drop the device's draft entirely (explicit close or after committing to the batch). */
export const clearDiscoverDraft = (deviceId) => {
  if (!deviceId) return;
  try {
    const s = store();
    if (s) s.removeItem(KEY(deviceId));
  } catch (e) {
    /* non-fatal */
  }
};
